import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../layout/Header';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Table, { TableRow, TableCell } from '../common/Table';
import payrollApi from '../../firebase/apis/payrollApi';
import { getCoaches } from '../../firebase/firestore';
import { Search, DollarSign, Users, Clock, CheckCircle, Download, ChevronDown, MapPin, Loader, Check, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { exportPayPeriodSummary, exportCoachHistory, exportByLocation, exportVolunteerHours } from '../../utils/exportUtils';
import { Shield } from '@mui/icons-material';

const statusColors = {
  approved: 'bg-blue-100 text-blue-800 border-blue-200',
  'ready for payroll': 'bg-purple-100 text-purple-800 border-purple-200',
  paid: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const color = statusColors[s] || 'bg-gray-100 text-gray-800 border-gray-200';
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${color}`}>
      {status || 'Pending'}
    </span>
  );
};

const CustomSelect = ({ value, onChange, options, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={`relative inline-block ${className || ''}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 flex items-center justify-between gap-2"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>{selected ? selected.label : placeholder}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-[220px] overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 transition-colors ${opt.value === value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Payroll = () => {
  const [activeTab, setActiveTab] = useState('staff');
  const [profiles, setProfiles] = useState([]);
  const [payPeriods, setPayPeriods] = useState([]);
  const [payrollEntries, setPayrollEntries] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Coach profile modal
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [coachHistory, setCoachHistory] = useState(null);

  // Compensation editor
  const [editComp, setEditComp] = useState(null);
  const [compForm, setCompForm] = useState({
    employmentType: 'paid_coach',
    jobTitle: '',
    hourlyRate: 0,
    payStatus: 'active',
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profileModalTab, setProfileModalTab] = useState('profile');

  // Pay period detail
  const [viewEntriesModal, setViewEntriesModal] = useState({ isOpen: false, periodName: '', entries: [] });
  const [allEntries, setAllEntries] = useState([]);

  // Mark Paid
  const [markPaidModal, setMarkPaidModal] = useState({ isOpen: false, type: '', id: '' });
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [submittingPayment, setSubmittingPayment] = useState(false);



  // Period selection
  const [selectedPeriodIds, setSelectedPeriodIds] = useState([]);

  // Period CRUD modal
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [periodForm, setPeriodForm] = useState({ startDate: '', endDate: '', label: '', payOutDate: '' });

  // Report
  const [reportType, setReportType] = useState('period-summary');
  const [reportPeriodId, setReportPeriodId] = useState('');
  const [reportCoachId, setReportCoachId] = useState('');
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [profilesData, periodsData, dashboardData] = await Promise.all([
        payrollApi.getProfiles().catch(() => []),
        payrollApi.getPayPeriods().catch(() => []),
        payrollApi.getDashboard().catch(() => null),
      ]);
      let profiles = Array.isArray(profilesData) ? profilesData : [];

      // Remove orphaned profiles (coach no longer exists) and auto-create missing ones
      try {
        const allCoaches = await getCoaches();
        const coachIds = new Set(allCoaches.map(c => c.id));

        // Remove profiles for deleted coaches
        const orphaned = profiles.filter(p => !coachIds.has(p.coachId));
        if (orphaned.length > 0) {
          await Promise.allSettled(orphaned.map(p => payrollApi.deleteProfile(p.coachId)));
          profiles = profiles.filter(p => coachIds.has(p.coachId));
        }

        // Auto-create profiles for approved coaches missing them
        const approvedCoaches = allCoaches.filter(
          c => c.approvalStatus === 'approved' || c.status === 'approved'
        );
        const profileCoachIds = new Set(profiles.map(p => p.coachId));
        const missing = approvedCoaches.filter(c => !profileCoachIds.has(c.id));
        if (missing.length > 0) {
          await Promise.allSettled(
            missing.map(c => payrollApi.autoCreateProfile(c.id))
          );
          const refreshed = await payrollApi.getProfiles();
          profiles = Array.isArray(refreshed) ? refreshed : [];
        }
      } catch (syncErr) {
        console.warn('Payroll profile sync failed:', syncErr);
      }

      setProfiles(profiles);
      setPayPeriods(Array.isArray(periodsData) ? periodsData : []);
      setDashboard(dashboardData);
    } catch (err) {
      console.error('Failed to load payroll data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (activeTab === 'pay-periods' || activeTab === 'staff') {
      loadAllEntries();
    }
  }, [activeTab]);

  const filteredProfiles = profiles.filter((p) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      (p.coachName || '').toLowerCase().includes(q) ||
      (p.coachEmail || '').toLowerCase().includes(q) ||
      (p.jobTitle || '').toLowerCase().includes(q)
    );
  });

  const handleOpenCoachProfile = async (profile, tab = 'profile') => {
    setSelectedCoach(profile);
    setIsProfileModalOpen(true);
    setProfileModalTab(tab);
    try {
      const history = await payrollApi.getCoachHistory(profile.coachId);
      setCoachHistory(history);
    } catch {
      setCoachHistory(null);
    }
  };

  const handleSaveCompensation = async () => {
    if (!selectedCoach) return;
    try {
      await payrollApi.updateProfile(selectedCoach.coachId, compForm);
      setSelectedCoach({ ...selectedCoach, ...compForm });
      setEditComp(null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      // Silently refresh profiles without triggering loading state
      payrollApi.getProfiles().then((data) => {
        if (Array.isArray(data)) setProfiles(data);
      }).catch(() => { });
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const handleOpenCompEditor = () => {
    if (!selectedCoach) return;
    setCompForm({
      employmentType: selectedCoach.employmentType || 'paid_coach',
      jobTitle: selectedCoach.jobTitle || '',
      hourlyRate: selectedCoach.hourlyRate || 0,
      payStatus: selectedCoach.payStatus || 'active',
    });
    setEditComp('compensation');
  };

  const handleViewPeriodEntries = async (period) => {
    const name = period.label || formatPeriodLabel(period);
    setViewEntriesModal({ isOpen: true, periodName: name, entries: [] });
    try {
      const entries = await payrollApi.getEntries({ payPeriodId: period.id });
      setViewEntriesModal(prev => ({ ...prev, entries: Array.isArray(entries) ? entries : [] }));
    } catch {
      setViewEntriesModal(prev => ({ ...prev, entries: [] }));
    }
  };

  const loadAllEntries = async () => {
    try {
      const entries = await payrollApi.getEntries({});
      setAllEntries(Array.isArray(entries) ? entries : []);
    } catch {
      setAllEntries([]);
    }
  };

  const handleOpenMarkPaidModal = (type, id) => {
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setMarkPaidModal({ isOpen: true, type, id });
  };

  const handleConfirmMarkPaid = async () => {
    if (submittingPayment) return;
    const { type, id } = markPaidModal;
    if (!paymentDate) { alert('Please select a payment date.'); return; }
    setSubmittingPayment(true);
    try {
      if (type === 'period') {
        await payrollApi.markPayPeriodPaid(id, { paymentDate });
        await loadData();
      } else {
        await payrollApi.markEntryPaid(id, { paymentDate });
      }
      await loadAllEntries();
      setMarkPaidModal({ isOpen: false, type: '', id: '' });
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleOpenCreatePeriod = () => {
    setEditingPeriod(null);
    setPeriodForm({ startDate: '', endDate: '', label: '', payOutDate: '' });
    setIsPeriodModalOpen(true);
  };

  const toDateInputValue = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const handleOpenEditPeriod = (period) => {
    setEditingPeriod(period);
    const sd = period.startDate ? parseDate(period.startDate) : null;
    const ed = period.endDate ? parseDate(period.endDate) : null;
    const pod = period.payOutDate ? parseDate(period.payOutDate) : null;
    setPeriodForm({
      startDate: toDateInputValue(sd),
      endDate: toDateInputValue(ed),
      label: period.label || '',
      payOutDate: toDateInputValue(pod),
    });
    setIsPeriodModalOpen(true);
  };
  const safeDate = (v) => {
    if (!v) return '—';

    const formatDate = (date) => {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${month}-${day}-${year}`;
    };

    if (typeof v === 'object' && '_seconds' in v) {
      return formatDate(new Date(v._seconds * 1000));
    }

    if (v && typeof v.toDate === 'function') {
      return formatDate(v.toDate());
    }

    if (v instanceof Date) {
      return formatDate(v);
    }

    if (typeof v === 'string') {
      const parsed = parseDate(v);
      if (parsed) return formatDate(parsed);
    }

    return v;
  };

  const parseDate = (v) => {
    if (!v) return null;
    if (typeof v === 'object' && '_seconds' in v) return new Date(v._seconds * 1000);
    if (typeof v.toDate === 'function') return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const [y, m, d] = v.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      return isNaN(date.getTime()) ? null : date;
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const handleSavePeriod = async (e) => {
    e.preventDefault();
    if (!periodForm.startDate || !periodForm.endDate) {
      alert('Please provide both start and end dates.');
      return;
    }
    if (new Date(periodForm.endDate) <= new Date(periodForm.startDate)) {
      alert('End date must be after start date.');
      return;
    }
    const data = {
      startDate: periodForm.startDate,
      endDate: periodForm.endDate,
      label: periodForm.label ? periodForm.label : `${safeDate(periodForm.startDate)} – ${safeDate(periodForm.endDate)}`,
      payOutDate: periodForm.payOutDate || null,
    };
    try {
      if (editingPeriod) {
        await payrollApi.updatePayPeriod(editingPeriod.id, data);
      } else {
        await payrollApi.createPayPeriod(data);
      }
      setIsPeriodModalOpen(false);
      setEditingPeriod(null);
      setViewEntriesModal({ isOpen: false, periodName: '', entries: [] });
      await loadData();
    } catch (err) {
      alert('Failed to save pay period: ' + err.message);
    }
  };

  const handleDeletePeriod = async (period) => {
    if (!window.confirm(`Delete pay period ${period.label || formatPeriodLabel(period)}? This cannot be undone.`)) return;
    try {
      setViewEntriesModal({ isOpen: false, periodName: '', entries: [] });
      await payrollApi.deletePayPeriod(period.id);
      await loadData();
    } catch (err) {
      alert('Failed to delete pay period: ' + err.message);
    }
  };

  const handleDeleteSelectedPeriods = async () => {
    const ids = selectedPeriodIds;
    if (ids.length === 0) { alert('No pay periods selected.'); return; }
    if (!window.confirm(`Delete ${ids.length} selected pay period(s) and related entries? This CANNOT be undone.`)) return;
    try {
      setLoading(true);
      for (const id of ids) {
        await payrollApi.deletePayPeriod(id);
      }
      setSelectedPeriodIds([]);
      await loadData();
      alert(`Deleted ${ids.length} pay period(s) successfully.`);
    } catch (err) {
      alert('Failed to delete pay periods: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPeriodSummary = async () => {
    if (!reportPeriodId) { alert('Select a pay period first.'); return; }
    setExporting(true);
    try {
      const data = await payrollApi.getReport('period-summary', { periodId: reportPeriodId });
      const period = data?.period;
      const entries = data?.entries || [];
      if (entries.length === 0) { alert('No entries for this pay period.'); return; }
      exportPayPeriodSummary(entries, period);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportCoachHistory = async () => {
    if (!reportCoachId) { alert('Select a coach first.'); return; }
    setExporting(true);
    try {
      const data = await payrollApi.getReport('coach-history', { coachId: reportCoachId });
      const entries = data?.entries || [];
      if (entries.length === 0) { alert('No pay history for this coach.'); return; }
      const coach = filteredProfiles.find((p) => p.coachId === reportCoachId);
      exportCoachHistory(entries, coach?.coachName);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportByLocation = async () => {
    if (!reportPeriodId) { alert('Select a pay period first.'); return; }
    setExporting(true);
    try {
      const data = await payrollApi.getReport('by-location', { periodId: reportPeriodId });
      const locations = data?.locations || [];
      if (locations.length === 0) { alert('No location data for this period.'); return; }
      exportByLocation(locations);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportVolunteerHours = async () => {
    if (!reportPeriodId) { alert('Select a pay period first.'); return; }
    setExporting(true);
    try {
      const data = await payrollApi.getReport('volunteer-hours', { periodId: reportPeriodId });
      const volunteers = data?.volunteers || [];
      if (volunteers.length === 0) { alert('No volunteer hours for this period.'); return; }
      exportVolunteerHours(volunteers);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (v) => `$${(v || 0).toFixed(2)}`;
  const formatHours = (v) => `${(v || 0).toFixed(1)} hrs`;

  // Safely render a value that could be a Firestore Timestamp, Date, or string


  const shortDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const yearDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatPeriodLabel = (period) => {
    if (!period) return '';
    const sd = parseDate(period.startDate);
    const ed = parseDate(period.endDate);
    if (!sd || !ed) return `${safeDate(period.startDate)} – ${safeDate(period.endDate)}`;
    if (sd.getFullYear() === ed.getFullYear() && sd.getMonth() === ed.getMonth()) {
      return `${shortDate(sd)}–${ed.getDate()}, ${sd.getFullYear()}`;
    }
    if (sd.getFullYear() === ed.getFullYear()) {
      return `${shortDate(sd)} – ${shortDate(ed)}, ${sd.getFullYear()}`;
    }
    return `${yearDate(sd)} – ${yearDate(ed)}`;
  };

  const [showLocationPayroll, setShowLocationPayroll] = useState(false);
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <span className="ml-3 text-lg">Loading payroll data...</span>
      </div>
    );
  }

  return (
    <div>
      <Header title="Payroll Management" subtitle="Manage coach compensation, pay periods, and payroll reports" />

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Total Payroll Due</span>
            <DollarSign size={20} className="text-blue-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard?.totalPayrollDue || 0)}</div>
          <p className="text-xs text-gray-500 mt-1">Current pay period</p>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Total Payroll Paid</span>
            <CheckCircle size={20} className="text-green-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(dashboard?.totalPayrollPaid || 0)}</div>
          <p className="text-xs text-gray-500 mt-1">Year to date</p>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Pending Timesheets</span>
            <Clock size={20} className="text-yellow-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{dashboard?.pendingTimesheets || 0}</div>
          <p className="text-xs text-gray-500 mt-1">Awaiting approval</p>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Active Staff</span>
            <Users size={20} className="text-purple-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{dashboard?.activeStaff || 0}</div>
          <p className="text-xs text-gray-500 mt-1">On payroll</p>
        </div>
      </div>

      {/* Payroll by Location Table */}
      <div className="glass rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">Payroll by Location</h3>
            <MapPin size={18} className="text-gray-400" />
          </div>

          <button
            type="button"
            onClick={() => setShowLocationPayroll(!showLocationPayroll)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors h3"
          >
            {showLocationPayroll ? (
              <>
                <EyeOff size={25} />
                Hide
              </>
            ) : (
              <>
                <Eye size={25} />
                Show
              </>
            )}
          </button>
        </div>

        {showLocationPayroll && (
          <>
            {dashboard?.byLocation && dashboard.byLocation.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Location
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Hours
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Staff
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Total Cost
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {dashboard.byLocation.map((loc, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">
                          {loc.location || "Unknown"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {formatHours(loc.hours)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {loc.staffCount || 0}
                        </td>
                        <td className="px-4 py-2 text-right font-medium">
                          {formatCurrency(loc.totalCost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td className="px-4 py-2">Total</td>
                      <td className="px-4 py-2 text-right">
                        {formatHours(
                          dashboard.byLocation.reduce(
                            (s, l) => s + (l.hours || 0),
                            0
                          )
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {dashboard.byLocation.reduce(
                          (s, l) => s + (l.staffCount || 0),
                          0
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {formatCurrency(
                          dashboard.byLocation.reduce(
                            (s, l) => s + (l.totalCost || 0),
                            0
                          )
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No location payroll data found.</p>
            )}
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-6">
            {['staff', 'pay-periods', 'reports'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab === 'staff' ? 'All Staff' : tab === 'pay-periods' ? 'Pay Periods' : 'Reports'}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* STAFF TAB */}
          {activeTab === 'staff' && (
            <div>
              <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center justify-between">
                <div className="relative w-full sm:w-64">
                  <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search coaches..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coach</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredProfiles.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                            {searchTerm ? 'No coaches match your search.' : 'No payroll profiles found. Approved coaches will appear here automatically.'}
                          </td>
                        </tr>
                      ) : (
                        filteredProfiles.map((profile) => {
                          const coachEntries = allEntries.filter((e) => e.coachId === profile.coachId || e.coachName === profile.coachName);
                          let derivedStatus = profile.payStatus === 'active' ? 'Active' : 'Inactive';
                          if (coachEntries.some((e) => e.status === 'submitted' || e.status === 'ready for payroll')) derivedStatus = 'Ready for Payroll';
                          else if (coachEntries.some((e) => e.status === 'approved')) derivedStatus = 'Approved';
                          else if (coachEntries.length > 0 && coachEntries.every((e) => e.status === 'paid')) derivedStatus = 'Paid';
                          return (
                            <tr key={profile.id || profile.coachId} className="hover:bg-gray-50 transition">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                    {(profile.coachName || 'C')[0]}
                                  </div>
                                  <div>
                                    <div>{profile.coachName || 'Unknown'}</div>
                                    <div className="text-xs text-gray-500">{profile.coachEmail}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                {profile.jobTitle || '—'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${profile.employmentType === 'volunteer' ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                  {profile.employmentType === 'volunteer' ? 'Volunteer' : 'Paid Coach'}
                                </span>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${derivedStatus === 'Ready for Payroll' ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                  : derivedStatus === 'Approved' ? 'bg-blue-100 text-blue-800 border-blue-200'
                                    : derivedStatus === 'Paid' ? 'bg-green-100 text-green-800 border-green-200'
                                      : 'bg-gray-100 text-gray-800 border-gray-200'
                                  }`}>
                                  {derivedStatus}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleOpenCoachProfile(profile, 'profile')}
                                    className="text-primary-600 hover:text-primary-800 font-medium whitespace-nowrap"
                                  >
                                    View Profile
                                  </button>
                                  <button
                                    onClick={() => handleOpenCoachProfile(profile, 'payouts')}
                                    className="text-green-600 hover:text-green-800 font-medium whitespace-nowrap"
                                  >
                                    View Payouts
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* PAY PERIODS TAB */}
          {activeTab === 'pay-periods' && (
            <div className="space-y-6">
              {/* Pay Period Management */}
              <div className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">All Pay Periods</h3>
                  <div className="flex gap-2">
                    <Button onClick={handleOpenCreatePeriod} className="text-sm">
                      Create Pay Period
                    </Button>
                    {selectedPeriodIds.length > 0 && (
                      <Button onClick={handleDeleteSelectedPeriods} variant="secondary" className="text-sm text-red-600 border-red-200 hover:bg-red-50">
                        Delete ({selectedPeriodIds.length})
                      </Button>
                    )}
                  </div>
                </div>
                {payPeriods.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No pay periods yet. Create one to get started.</p>
                ) : (
                  <div className="overflow-x-auto -mx-5">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-y border-gray-200">
                          <th className="px-3 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={payPeriods.length > 0 && payPeriods.every(p => selectedPeriodIds.includes(p.id))}
                              onChange={() => {
                                if (payPeriods.every(p => selectedPeriodIds.includes(p.id))) {
                                  setSelectedPeriodIds([]);
                                } else {
                                  setSelectedPeriodIds(payPeriods.map(p => p.id));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                            />
                          </th>
                          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay Period Name</th>
                          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay Out Date</th>
                          <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {payPeriods.map((period) => (
                          <tr key={period.id} className={`hover:bg-gray-50 transition ${selectedPeriodIds.includes(period.id) ? 'bg-blue-50' : ''}`}>
                            <td className="px-3 py-3">
                              <input
                                type="checkbox"
                                checked={selectedPeriodIds.includes(period.id)}
                                onChange={() => {
                                  setSelectedPeriodIds(prev =>
                                    prev.includes(period.id) ? prev.filter(id => id !== period.id) : [...prev, period.id]
                                  );
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                              />
                            </td>
                            <td className="px-5 py-3 font-medium text-gray-900">{period.label || ''}</td>
                            <td className="px-5 py-3 text-gray-700">{safeDate(period.startDate)}</td>
                            <td className="px-5 py-3 text-gray-700">{safeDate(period.endDate)}</td>
                            <td className="px-5 py-3 text-gray-700">{safeDate(period.payOutDate)}</td>
                            <td className="px-5 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleOpenMarkPaidModal('period', period.id)}
                                  className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition"
                                  title="Mark as Paid"
                                >
                                  <DollarSign size={16} />
                                </button>
                                <button
                                  onClick={() => handleViewPeriodEntries(period)}
                                  className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition"
                                  title="View entries for this period"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  onClick={() => handleOpenEditPeriod(period)}
                                  className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition"
                                  title="Edit pay period"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeletePeriod(period)}
                                  className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition"
                                  title="Delete pay period"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* REPORTS TAB */}
          {activeTab === 'reports' && (
            <div>
              <p className="text-sm text-gray-600 mb-4">Export payroll data for QuickBooks and record keeping.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-5">
                  <h3 className="font-semibold mb-3">Pay Period Summary</h3>
                  <p className="text-sm text-gray-600 mb-3">All coaches, hours, and amounts for a selected pay period.</p>
                  <div className="space-y-3">
                    <CustomSelect
                      value={reportPeriodId}
                      onChange={setReportPeriodId}
                      placeholder="Select pay period..."
                      options={payPeriods.map((p) => ({ value: p.id, label: formatPeriodLabel(p) }))}
                      className="w-full"
                    />
                    <Button onClick={handleExportPeriodSummary} disabled={!reportPeriodId || exporting} className="text-sm">
                      {exporting ? <Loader size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />}
                      Export .xlsx
                    </Button>
                    {reportPeriodId && (
                      <ul className="text-xs text-gray-500 space-y-0.5 mt-2 list-disc pl-4">
                        <li>Coach Name, Job Title, Employment Type, Location</li>
                        <li>Pay Period, Total Hours, Hourly Rate, Amount Owed</li>
                        <li>Payment Status, Pay Date, Notes</li>
                      </ul>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-5">
                  <h3 className="font-semibold mb-3">Per-Coach History</h3>
                  <p className="text-sm text-gray-600 mb-3">All pay periods for one specific coach.</p>
                  <div className="space-y-3">
                    <CustomSelect
                      value={reportCoachId}
                      onChange={setReportCoachId}
                      placeholder="Select coach..."
                      options={filteredProfiles.map((p) => ({ value: p.coachId, label: p.coachName }))}
                      className="w-full"
                    />
                    <Button onClick={handleExportCoachHistory} disabled={!reportCoachId || exporting} className="text-sm">
                      {exporting ? <Loader size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />}
                      Export .xlsx
                    </Button>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-5">
                  <h3 className="font-semibold mb-3">Payroll by Location</h3>
                  <p className="text-sm text-gray-600 mb-3">Hours and total payroll cost broken down by site.</p>
                  <div className="space-y-3">
                    <CustomSelect
                      value={reportPeriodId}
                      onChange={setReportPeriodId}
                      placeholder="Select pay period..."
                      options={payPeriods.map((p) => ({ value: p.id, label: formatPeriodLabel(p) }))}
                      className="w-full"
                    />
                    <Button onClick={handleExportByLocation} disabled={!reportPeriodId || exporting} className="text-sm">
                      {exporting ? <Loader size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />}
                      Export .xlsx
                    </Button>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-5">
                  <h3 className="font-semibold mb-3">Volunteer Hours</h3>
                  <p className="text-sm text-gray-600 mb-3">All volunteer staff with total hours (no dollar amounts).</p>
                  <div className="space-y-3">
                    <CustomSelect
                      value={reportPeriodId}
                      onChange={setReportPeriodId}
                      placeholder="Select pay period..."
                      options={payPeriods.map((p) => ({ value: p.id, label: formatPeriodLabel(p) }))}
                      className="w-full"
                    />
                    <Button onClick={handleExportVolunteerHours} disabled={!reportPeriodId || exporting} className="text-sm">
                      {exporting ? <Loader size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />}
                      Export .xlsx
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Coach Profile Modal */}
      <Modal
        isOpen={isProfileModalOpen}
        onClose={() => { setIsProfileModalOpen(false); setSelectedCoach(null); setCoachHistory(null); setEditComp(null); setProfileModalTab('profile'); }}
        title={selectedCoach ? `Payroll Profile — ${selectedCoach.coachName}` : ''}
        size="xl"
      >
        {selectedCoach && (
          <div className="space-y-6">
            {/* Sub-tabs */}
            <div className="flex border-b border-gray-200 -mx-6 px-6">
              <button
                onClick={() => setProfileModalTab('profile')}
                className={`py-3 text-sm font-medium border-b-2 transition-colors mr-6 ${profileModalTab === 'profile'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                View Profile
              </button>
              <button
                onClick={() => setProfileModalTab('payouts')}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${profileModalTab === 'payouts'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                View Payouts
              </button>
            </div>

            {profileModalTab === 'profile' ? (
              <>
                {/* Coach Info */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div><span className="text-gray-500">Name:</span> <span className="font-medium ml-1">{selectedCoach.coachName}</span></div>
                    <div><span className="text-gray-500">Email:</span> <span className="font-medium ml-1">{selectedCoach.coachEmail}</span></div>
                    <div><span className="text-gray-500">Phone:</span> <span className="font-medium ml-1">{selectedCoach.coachPhone || '—'}</span></div>
                  </div>
                </div>

                {/* Compensation Settings */}
                {editComp === 'compensation' ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-3">Edit Compensation Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                        <select
                          value={compForm.employmentType}
                          onChange={(e) => setCompForm({ ...compForm, employmentType: e.target.value })}
                          className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                        >
                          <option value="paid_coach">Paid Coach</option>
                          <option value="volunteer">Volunteer</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                        <input
                          type="text"
                          value={compForm.jobTitle}
                          onChange={(e) => setCompForm({ ...compForm, jobTitle: e.target.value })}
                          placeholder="e.g. Head Coach, Site Manager"
                          className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={compForm.hourlyRate}
                          onChange={(e) => setCompForm({ ...compForm, hourlyRate: parseFloat(e.target.value) })}
                          className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                          disabled={compForm.employmentType === 'volunteer'}
                        />
                        {compForm.employmentType === 'volunteer' && (
                          <p className="text-xs text-gray-500 mt-1">Volunteers default to $0.00/hr</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pay Status</label>
                        <select
                          value={compForm.payStatus}
                          onChange={(e) => setCompForm({ ...compForm, payStatus: e.target.value })}
                          className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      {saveSuccess ? (
                        <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium py-2 px-3">
                          <Check size={16} /> Changes saved
                        </span>
                      ) : (
                        <Button onClick={handleSaveCompensation} className="text-sm">Save Changes</Button>
                      )}
                      <Button onClick={() => setEditComp(null)} variant="secondary" className="text-sm">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold">Compensation Settings</h4>
                      <button onClick={handleOpenCompEditor} className="text-sm text-primary-600 hover:text-primary-800 font-medium">
                        Edit
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Type:</span>
                        <span className="ml-1 font-medium">{selectedCoach.employmentType === 'volunteer' ? 'Volunteer' : 'Paid Coach'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Job Title:</span>
                        <span className="ml-1 font-medium">{selectedCoach.jobTitle || '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Rate:</span>
                        <span className="ml-1 font-medium">
                          {selectedCoach.employmentType === 'volunteer' ? '$0.00/hr' : `$${(selectedCoach.hourlyRate || 0).toFixed(2)}/hr`}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <span className="ml-1">
                          {(() => {
                            const coachEntries = allEntries.filter((e) => e.coachId === selectedCoach.coachId || e.coachName === selectedCoach.coachName);
                            let s = selectedCoach.payStatus === 'active' ? 'Active' : 'Inactive';
                            if (coachEntries.some((e) => e.status === 'submitted' || e.status === 'ready for payroll')) s = 'Ready for Payroll';
                            else if (coachEntries.some((e) => e.status === 'approved')) s = 'Approved';
                            else if (coachEntries.length > 0 && coachEntries.every((e) => e.status === 'paid')) s = 'Paid';
                            return <StatusBadge status={s} />;
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lifetime Totals */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Lifetime Totals</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Lifetime Hours:</span>
                      <span className="ml-1 font-bold text-md">{formatHours(selectedCoach.lifetimeHours)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Lifetime Earnings:</span>
                      <span className="ml-1 font-bold text-md">{formatCurrency(selectedCoach.lifetimeEarnings)}</span>
                    </div>
                    {(() => {
                      const paidEntries = (coachHistory?.entries || [])
                        .filter(e => e.status === 'paid')
                        .sort((a, b) => {
                          const da = parseDate(a.paymentDate || a.date);
                          const db = parseDate(b.paymentDate || b.date);
                          return (db || 0) - (da || 0);
                        });
                      const lastPaid = paidEntries[0];
                      const lastDate = lastPaid ? safeDate(lastPaid.paymentDate || lastPaid.date) : '—';
                      const lastAmount = lastPaid ? formatCurrency(lastPaid.amountOwed) : '—';
                      return (
                        <>
                          <div>
                            <span className="text-gray-500">Last Payment Date:</span>
                            <span className="ml-1 font-bold text-md">{lastDate}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Last Amount Paid:</span>
                            <span className="ml-1 font-bold text-md">{lastAmount}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Pay History */}
                {/* <div>
                  <h4 className="font-semibold mb-3">Pay History</h4>
                  <Table
                    headers={['Pay Period', 'Approval Date', 'Hours', 'Rate', 'Amount', 'Status']}
                  >
                    {!coachHistory || !coachHistory.entries || coachHistory.entries.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-4">No pay history yet.</TableCell></TableRow>
                    ) : (
                      coachHistory.entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{entry.payPeriodId || '—'}</TableCell>
                          <TableCell>{safeDate(entry.date)}</TableCell>
                          <TableCell>{formatHours(entry.hours)}</TableCell>
                          <TableCell>{entry.employmentType === 'volunteer' ? 'Volunteer' : `$${(entry.rate || 0).toFixed(2)}/hr`}</TableCell>
                          <TableCell className="font-medium">{formatCurrency(entry.amountOwed)}</TableCell>
                          <TableCell><StatusBadge status={entry.status === 'paid' ? 'Paid' : entry.status === 'approved' ? 'Approved' : entry.status === 'submitted' || entry.status === 'ready for payroll' ? 'Ready for Payroll' : entry.status} /></TableCell>
                        </TableRow>
                      ))
                    )}
                  </Table>
                </div> */}


              </>
            ) : (
              /* View Payouts Tab */
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Rate:</span>
                      <span className="ml-1 font-semibold">
                        {selectedCoach.employmentType === 'volunteer' ? '$0.00/hr' : `$${(selectedCoach.hourlyRate || 0).toFixed(2)}/hr`}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Pending Hours:</span>
                      <span className={`ml-1 font-semibold ${selectedCoach.pendingHours > 0 ? 'text-amber-600' : ''}`}>{formatHours(selectedCoach.pendingHours)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Est. Earnings:</span>
                      <span className={`ml-1 font-semibold ${selectedCoach.estimatedEarnings > 0 ? 'text-amber-600' : ''}`}>{formatCurrency(selectedCoach.estimatedEarnings)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Lifetime Hours:</span>
                      <span className="ml-1 font-semibold">{formatHours(selectedCoach.lifetimeHours)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Lifetime Earnings:</span>
                      <span className="ml-1 font-semibold">{formatCurrency(selectedCoach.lifetimeEarnings)}</span>
                    </div>
                  </div>
                </div>

                <h4 className="font-semibold mb-3">Payout History</h4>
                {!coachHistory || !coachHistory.entries || coachHistory.entries.filter(e => e.status == 'approved').length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No payout history yet.</p>
                ) : (
                  (() => {
                    const paidEntries = coachHistory.entries.filter(e => e.status == 'approved' || e.status == 'paid');
                    const grouped = {};
                    paidEntries.forEach(entry => {
                      const pid = entry.payPeriodId || '__none__';
                      if (!grouped[pid]) grouped[pid] = { entries: [], totalAmount: 0, approvedAt: null, status: null };
                      grouped[pid].entries.push(entry);
                      grouped[pid].totalAmount += entry.amountOwed || 0;
                      grouped[pid].status = entry.status == "approved" || entry.status == "paid" ? "Paid" : grouped[pid].status;
                      if (entry.approvedAt) {
                        const parsed = parseDate(entry.approvedAt);
                        if (parsed && (!grouped[pid].approvedAt || parsed > grouped[pid].approvedAt)) {
                          grouped[pid].approvedAt = parsed;
                        }
                      }
                    });
                    const payPeriodMap = {};
                    payPeriods.forEach(p => { payPeriodMap[p.id] = p; });
                    const sortedPeriodIds = Object.keys(grouped).sort((a, b) => {
                      const pa = a === '__none__' ? null : payPeriodMap[a];
                      const pb = b === '__none__' ? null : payPeriodMap[b];
                      const da = pa ? parseDate(pa.startDate || pa.endDate) : null;
                      const db = pb ? parseDate(pb.startDate || pb.endDate) : null;
                      return (db || 0) - (da || 0);
                      console.log(payPeriods)
                    });
                    return (
                      <Table
                        headers={['Pay Period', 'Approve Date', 'Total Hours', 'Amount Paid', 'Status']}
                      >
                        {sortedPeriodIds.map(pid => {
                          const group = grouped[pid];
                          const period = pid === '__none__' ? null : payPeriodMap[pid];
                          const periodLabel = period ? (period.label || formatPeriodLabel(period)) : 'Unknown Period';
                          const periodDates = period ? `${safeDate(period.startDate)} – ${safeDate(period.endDate)}` : '—';
                          const totalHours = group.entries.reduce((s, e) => s + (e.hours || 0), 0);
                          return (
                            <TableRow key={pid}>
                              <TableCell>
                                <div className="font-medium">{periodLabel}</div>
                                <div className="text-xs text-gray-500">{periodDates}</div>
                              </TableCell>
                              <TableCell>{group.approvedAt ? yearDate(group.approvedAt) : '—'}</TableCell>
                              <TableCell>{formatHours(totalHours)}</TableCell>
                              <TableCell className="font-medium">{formatCurrency(group.totalAmount)}</TableCell>
                              <TableCell>
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${group.status === "Paid"
                                    ? "bg-green-50 text-green-700 border border-green-200"
                                    : group.status === "approved"
                                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                                      : group.status === "pending"
                                        ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                                        : "bg-gray-50 text-gray-700 border border-gray-200"
                                    }`}
                                >
                                  {group.status || "—"}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </Table>
                    );
                  })()
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Mark as Paid Modal */}
      {markPaidModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Mark as Paid</h2>
            <p className="text-sm text-gray-600 mb-4">
              {markPaidModal.type === 'period'
                ? 'Set the payment date for all entries in this pay period.'
                : 'Set the payment date for this entry.'}
            </p>
            <div className="flex flex-col mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1">Payment Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 w-full"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setMarkPaidModal({ isOpen: false, type: '', id: '' })}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMarkPaid}
                disabled={!paymentDate || submittingPayment}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingPayment ? 'Processing…' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Pay Period Modal */}
      {isPeriodModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => { setIsPeriodModalOpen(false); setEditingPeriod(null); }}>
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">{editingPeriod ? 'Edit Pay Period' : 'Create Pay Period'}</h2>
            <form onSubmit={handleSavePeriod}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Period Name <span className="text-gray-400">(optional)</span></label>
                  <input
                    type="text"
                    value={periodForm.label}
                    onChange={(e) => setPeriodForm({ ...periodForm, label: e.target.value })}
                    placeholder="e.g. May 2025, Summer Session"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={periodForm.startDate}
                    onChange={(e) => {
                      const startDate = e.target.value;
                      const formattedStart = safeDate(startDate);
                      const formattedEnd = safeDate(periodForm.endDate);

                      setPeriodForm({
                        ...periodForm,
                        startDate,
                        label: formattedEnd
                          ? `${formattedStart} – ${formattedEnd}`
                          : formattedStart,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={periodForm.endDate}
                    onChange={(e) => {
                      const endDate = e.target.value;
                      const formattedStart = safeDate(periodForm.startDate);
                      const formattedEnd = safeDate(endDate);

                      setPeriodForm({
                        ...periodForm,
                        endDate,
                        label: formattedStart
                          ? `${formattedStart} – ${formattedEnd}`
                          : formattedEnd,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Out Date</label>
                  <input
                    type="date"
                    value={periodForm.payOutDate}
                    onChange={(e) => setPeriodForm({ ...periodForm, payOutDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setIsPeriodModalOpen(false); setEditingPeriod(null); }}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition text-sm"
                >
                  Cancel
                </button>
                <Button type="submit" className="text-sm">
                  {editingPeriod ? 'Save Changes' : 'Create Period'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Period Entries Modal */}
      {viewEntriesModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setViewEntriesModal({ isOpen: false, periodName: '', entries: [] })}>
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Entries — {viewEntriesModal.periodName}</h2>
              <button
                onClick={() => setViewEntriesModal({ isOpen: false, periodName: '', entries: [] })}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {viewEntriesModal.entries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Coach</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {viewEntriesModal.entries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900">{entry.coachName || 'Unknown'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-700">{entry.jobTitle || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-700">{entry.location || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-700">{entry.hours || 0}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                          {entry.employmentType === 'volunteer' ? '$0' : `$${(entry.rate || 0).toFixed(2)}/hr`}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900">
                          {entry.employmentType === 'volunteer' ? '$0.00' : `$${(entry.amountOwed || 0).toFixed(2)}`}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${entry.status === 'submitted' ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                            : entry.status === 'ready for payroll' ? 'bg-purple-100 text-purple-800 border-purple-200'
                              : entry.status === 'approved' ? 'bg-blue-100 text-blue-800 border-blue-200'
                                : entry.status === 'paid' ? 'bg-green-100 text-green-800 border-green-200'
                                  : 'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                            {entry.status === 'submitted' || entry.status === 'ready for payroll' ? 'Ready for Payroll'
                              : entry.status === 'approved' ? 'Approved'
                                : entry.status === 'paid' ? 'Paid'
                                  : entry.status || 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center">
                          {entry.status !== 'paid' && (
                            <button
                              onClick={() => { setViewEntriesModal({ isOpen: false, periodName: '', entries: [] }); handleOpenMarkPaidModal('entry', entry.id); }}
                              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition"
                              title="Mark as Paid"
                            >
                              <DollarSign size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No entries in this pay period.</p>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Payroll;